import React, { useState } from 'react'
import {
  ProxyGroup,
  ProxySearchResult,
  MODE_CONFIG,
  ProxyMode,
} from '../../../../components/common/config/adminConfig'
import LabeledValue from '../../../../components/common/LabeledValue'
import SearchForm, { SearchOption, FieldConfig } from './SearchForm'
import Table from '../../../../components/common/Table/Table'
import Modal from '../../../../components/common/Modal/Modal'
import Loader from '../../../../components/common/Loader'
import DateCell from './DateCell'
import type { CurrentProxyRecord } from '../../../../store/slices/admin/requestorProxySlice'
import type { AvailableEmployeeRecord } from '../../../../store/slices/admin/approverProxySlice'
import { useProxyModeState } from './useProxyModeState'
import { useProxySave } from './useProxySave'
import { useEmployeeSearch } from './useEmployeeSearch'
import {
  EditablePerson,
  TrimmedEmployeeSearch,
  toEditableFromProxy,
  toEditableFromEmployee,
  todayMMDDYYYY,
  getMaxEndDate,
  validateProxyDates,
  buildEditValidationErrors,
  extractNuidFromName,
  buildApproverColumns,
  EDIT_PANEL_RESULT_COLUMNS,
} from './ProxyEditPanelUtils'


interface Props {
  row: ProxySearchResult | null
  // eslint-disable-next-line react/no-unused-prop-types
  groups?: ProxyGroup[]
  /** Mode determines the panel title and background colour. */
  mode?: ProxyMode
  /** Current proxy records fetched from the API on Edit click. */
  currentProxies: CurrentProxyRecord[]
  /** True while the fetchCurrentRP API call is in-flight. */
  isLoadingFetchCurrent: boolean
}

/** Synchronizes the local employee table when Redux search results change. */
const syncEmployeeResults = (
  seededEmployees: AvailableEmployeeRecord[],
  employeeResults: AvailableEmployeeRecord[],
  setSeededEmployees: React.Dispatch<
    React.SetStateAction<AvailableEmployeeRecord[]>
  >,
  setEmployees: React.Dispatch<React.SetStateAction<EditablePerson[]>>
): void => {
  if (seededEmployees !== employeeResults) {
    setSeededEmployees(employeeResults)
    setEmployees(employeeResults.map(toEditableFromEmployee))
  }
}

/**
 * Edit panel for managing proxy assignments for a selected approval group.
 * Displays current proxies fetched from the API, allows searching for employees
 * to add, and surfaces Save / Cancel actions with confirmation modals.
 */
const ProxyEditPanel: React.FC<Props> = ({
  row,
  mode = 'approver',
  currentProxies,
  isLoadingFetchCurrent,
}: Props) => {
  const [searchType, setSearchType] = useState<string>('name')
  const [nuid, setNuid] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [editSearchPerformed, setEditSearchPerformed] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showConfirmSaveModal, setShowConfirmSaveModal] = useState(false)
  /** Per-person end-date validation errors, keyed by EditablePerson.id. */
  const [dateErrors, setDateErrors] = useState<Record<string, string>>({})
  const [clearSearchResults, setClearSearchResults] = useState(false)

  // ProxyMode has exactly three values; 'requestor' is the only non-date-editable one.
  const isDateEditableMode = mode !== 'requestor'

  // Approval group the candidate would be added to (digits-only per contract).
  const approvalGrpId = String(row?.approvalGrpId ?? row?.groupId ?? '').trim()

  // Mode-specific Redux state via custom hook
  const {
    employeeResults,
    isLoadingEmployeeSearch,
    employeeSearchPerformed,
    employeeSearchError,
    isLoadingSave,
    saveError,
  } = useProxyModeState(mode)

  // Mode-specific save handler via custom hook
  const { handleSave: handleSaveProxy } = useProxySave(mode)

  // Mode-specific employee search via custom hook
  const { dispatchEmployeeSearch } = useEmployeeSearch(mode, approvalGrpId)

  // ── Local working copies of both tables ─────────────────────────────────
  // Rows can move between the "Approver Group" and "Search Results" tables
  // (UI-only). Each copy is seeded from its source and re-seeded — via the
  // React "adjust state when a prop changes" idiom — only when a fresh
  // fetch/search replaces the source reference, so moves persist in between.
  const [approverGroup, setApproverGroup] = useState<EditablePerson[]>(() =>
    currentProxies.map(toEditableFromProxy)
  )
  const [seededProxies, setSeededProxies] = useState(currentProxies)
  // Start with an empty list so stale Redux employee-search results from a
  // previous edit session are never shown on initial mount.
  const [employees, setEmployees] = useState<EditablePerson[]>([])
  const [seededEmployees, setSeededEmployees] = useState(employeeResults)
  /** Tracks the previous `row` so search state can be reset when the user edits a different record. */
  const [prevRow, setPrevRow] = useState(row)
  const [editingCell, setEditingCell] = useState<{
    rowId: string
    columnKey: string
  } | null>(null)

  // ── Reset search form + results when the user opens a different row for editing.
  if (prevRow !== row) {
    setPrevRow(row)
    setSearchType('name')
    setFirstName('')
    setLastName('')
    setNuid('')
    setErrors({})
    setEditSearchPerformed(false)
    // Clear local employee list and re-sync the seeded tracker so stale results
    // from the previous edit are not re-applied by the seeding guard below.
    setEmployees([])
    setSeededEmployees(employeeResults)
  }

  if (seededProxies !== currentProxies) {
    setSeededProxies(currentProxies)
    setApproverGroup(currentProxies.map(toEditableFromProxy))
    setDateErrors({})
  }
  syncEmployeeResults(
    seededEmployees,
    employeeResults,
    setSeededEmployees,
    setEmployees
  )

  if (!row) return <div className="p3">Select a user to edit</div>

  const { title, bgColor } = MODE_CONFIG[mode]

  const searchOptions: SearchOption[] = [
    { key: 'name', label: 'Employee First or Last Name' },
    { key: 'nuid', label: 'Employee NUID' },
  ]

  const fieldsByMode: Record<string, string[]> = {
    name: ['firstName', 'lastName'],
    nuid: ['nuid'],
  }

  const fieldMeta: Record<string, FieldConfig> = {
    firstName: {
      id: 1,
      label: 'First Name',
      name: 'firstName',
      placeholder: 'Enter first name',
      rowGroup: 'names',
      containerClass: 'w-full desktop:w-[350px]',
    },
    lastName: {
      id: 2,
      label: 'Last Name',
      name: 'lastName',
      placeholder: 'Enter last name',
      rowGroup: 'names',
      containerClass: 'w-full desktop:w-[350px]',
    },
    nuid: {
      id: 3,
      label: 'Employee NUID',
      name: 'nuid',
      placeholder: 'Enter NUID',
    },
  }

  const values = { firstName, lastName, nuid }

  /** Clears validation errors and updates the active search mode. */
  const handleSearchTypeChange = (type: string) => {
    setSearchType(type)
    setErrors({})
    setNuid('')
    setFirstName('')
    setLastName('')
  }

  /** Updates the individual field value and clears any error for that field. */
  const handleFieldChange = (fieldName: string, value: string) => {
    /** Lookup map of field name → state setter; avoids chained if/else branches. */
    const fieldSetters: Record<string, (v: string) => void> = {
      firstName: setFirstName,
      lastName: setLastName,
      nuid: setNuid,
    }
    fieldSetters[fieldName]?.(value)

    setErrors((prev) => {
      const { [fieldName]: _removed, ...rest } = prev
      return rest
    })
  }

  /**
   * Updates the date (start or end) for an approver-group row and validates that
   * the end date is strictly after the start date.
   *
   * @param personId - Id of the approver-group row to update.
   * @param field - Which date field to update ('startDate' or 'endDate').
   * @param value - The new MM/DD/YYYY date string.
   */
  const handleDateChange = (
    personId: string,
    field: 'startDate' | 'endDate',
    value: string
  ) => {
    const normalizedValue = value.trim()

    setApproverGroup((prev) =>
      prev.map((person) => {
        if (person.id !== personId) return person

        if (field === 'startDate') {
          return {
            ...person,
            startDate: value,
            endDate: normalizedValue ? getMaxEndDate(normalizedValue) : '',
          }
        }

        return {
          ...person,
          endDate: value,
        }
      })
    )

    const person = approverGroup.find((item) => item.id === personId)

    const startDate =
      field === 'startDate' ? normalizedValue : (person?.startDate ?? '')

    const endDate = field === 'endDate' ? value : (person?.endDate ?? '')

    const error = validateProxyDates(startDate, endDate)

    setDateErrors((prev) => {
      if (!error) {
        const { [personId]: _, ...rest } = prev
        return rest
      }

      return {
        ...prev,
        [personId]: error,
      }
    })
  }

  /**
   * Handles the edit-panel search submission: validates fields, then dispatches
   * the appropriate employee-search thunk based on the current mode.
   *
   * @param e - The form submit event.
   */
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed: TrimmedEmployeeSearch = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      nuid: nuid.trim(),
    }

    const validationErrors = buildEditValidationErrors(trimmed, searchType)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setErrors({})
    setEditSearchPerformed(true)
    dispatchEmployeeSearch(trimmed, searchType)
    setClearSearchResults(false)
  }

  /**
   * Moves a record from the "Approver Group" table into the "Search Results"
   * table. UI-only — no API call is made until the panel is saved.
   *
   * @param personId - Id of the approver-group row to move.
   */
  const handleRemoveFromGroup = (personId: string) => {
    const person = approverGroup.find((p) => p.id === personId)
    if (!person) return
    setApproverGroup((prev) => prev.filter((p) => p.id !== personId))
    setEmployees((prev) =>
      prev.some((p) => p.id === personId) ? prev : [...prev, person]
    )
    setDateErrors((prev) => {
      const { [personId]: _cleared, ...rest } = prev
      return rest
    })
  }

  /**
   * Moves a record from the "Search Results" table into the "Approver Group"
   * table. UI-only — no API call is made until the panel is saved.
   *
   * @param personId - Id of the search-result row to move.
   */
  const handleAddToGroup = (personId: string) => {
    const person = employees.find((p) => p.id === personId)
    if (!person) return

    const nuid = extractNuidFromName(person.name, person.id)

    setEmployees((prev) => prev.filter((p) => p.id !== personId))

    setApproverGroup((prev) =>
      prev.some((p) => p.id === nuid)
        ? prev
        : [
            ...prev,
            {
              ...person,
              id: nuid,
              startDate: todayMMDDYYYY(),
              endDate: getMaxEndDate(todayMMDDYYYY()),
              // Mark as newly added so assignApproverProxies / assignFloatEmployees
              // can set existingApproverProxy = "1" for this entry.
              isNewlyAdded: true,
            },
          ]
    )
  }

  /** Resets the search form fields to their initial empty state. */
  const resetSearchForm = () => {
    setSearchType('name')
    setFirstName('')
    setLastName('')
    setNuid('')
    setErrors({})
  }

  /** Clears employee search results and related UI state. */
  const clearEmployeeSearchState = () => {
    setEditSearchPerformed(false)
    setEmployees([])
    setClearSearchResults(true)
    setSeededEmployees(employeeResults)
  }

  /**
   * Reverts ALL unsaved changes on cancel confirmation:
   *   - Restores the approver group to the state originally loaded from the API
   *     (undoes adds, removes, and date edits).
   *   - Resets the search form and clears search results.
   */
  const handleCancelConfirm = () => {
    setApproverGroup(currentProxies.map(toEditableFromProxy))
    setDateErrors({})
    resetSearchForm()
    clearEmployeeSearchState()
    setShowCancelModal(false)
  }
  // Requestor Proxy has no End Date data in the DB (fetchCurrentRP never
  // populates it), so the column is only shown in Approver and Float modes, which have
  // real from/to dates.
  const approverColumns = buildApproverColumns(mode, isDateEditableMode)
  const resultColumns = EDIT_PANEL_RESULT_COLUMNS

  /**
   * Map the local employee working copy to result-table row shape. The Add
   * control moves the row into the Approver Group table (handled via the
   * table's `onActionClick`); the visual is a plain icon + label.
   */
  const employeeRows = employees.map((person) => ({
    id: person.id,
    empName: person.name,
    region: person.region,
    department: person.department,
    email: person.email || 'N/A',
    action: (
      <span className="inline-flex items-center gap-1 md:gap-2">
        <img
          src="/assets/images/plus-circle-outline.svg"
          alt="Add to Approver Group"
          width={24}
          height={24}
          className="shrink-0"
        />
        <span className=" md:inline text-[#0074AD] p3 -medium whitespace-nowrap">Add to Approver Group</span>
      </span>
    ),
  }))

  // Empty state: show the no-results banner only when the raw search returned
  // nothing AND no records have been moved in from the approver-group table.
  const showNoEmployeesFound =
    employeeSearchPerformed &&
    !isLoadingEmployeeSearch &&
    !employeeSearchError &&
    employeeResults.length === 0 &&
    employees.length === 0

  // Show the results section once a search runs or a record has been moved in.
  const showResultsSection =
    !clearSearchResults && (editSearchPerformed || employees.length > 0)

  /**
   * Renders a date cell for the approver group table using the DateCell component.
   */
  const renderDateCell = (
    person: EditablePerson,
    field: 'startDate' | 'endDate'
  ) => {
    const isEditing =
      isDateEditableMode &&
      editingCell?.rowId === person.id &&
      editingCell?.columnKey === field

    return (
      <DateCell
        person={person}
        field={field}
        isEditable={isDateEditableMode}
        isEditing={isEditing}
        dateError={field === 'endDate' ? dateErrors[person.id] : undefined}
        onEdit={() =>
          setEditingCell({
            rowId: person.id,
            columnKey: field,
          })
        }
        onChange={handleDateChange}
      />
    )
  }

  const proxyRows = approverGroup.map((person) => ({
    id: person.id,
    proxy: person.name,
    region: person.region,
    department: person.department,
    startDate: renderDateCell(person, 'startDate'),
    endDate: renderDateCell(person, 'endDate'),
    email: person.email || 'N/A',
    action: (
      <span className="inline-flex items-center gap-1 md:gap-2">
        <img
          src="/assets/images/minus-circle-solid.svg"
          alt="Remove from Approver Group"
          width={24}
          height={24}
          className="shrink-0"
        />
        <span className=" md:inline text-[#0074AD] p3 -medium whitespace-nowrap">
          Remove from Approver Group
        </span>
      </span>
    ),
  }))

  // In approver and float modes, disable Save when any proxy row has empty dates or a date validation error.
  // In requestor mode, dates are read-only so no date-based disabling applies.
  const hasMissingDates = approverGroup.some((p) => !p.startDate || !p.endDate)
  const isSaveDisabled =
    isLoadingSave ||
    (isDateEditableMode && (hasMissingDates || Object.keys(dateErrors).length > 0))

  /**
   * Executes the save after all confirmations have passed. Called directly when
   * the proxy list is non-empty, or from the confirm-save modal when it is empty.
   */
  const executeSave = async () => {
    const success = await handleSaveProxy({
      approvalGrpId,
      regionCode: row.regionCode ?? '',
      primaryApproverName: row.approver ?? '',
      approverGroup,
    })

    if (success) {
      setShowSuccessModal(true)
    }
  }

  /**
   * Handles the Save button click. When the proxy list is empty, shows a
   * confirmation modal before saving. Otherwise saves immediately.
   */
  const handleSave = () => {
    if (approverGroup.length === 0) {
      setShowConfirmSaveModal(true)
    } else {
      void executeSave()
    }
  }

  return (
    <>
      {(isLoadingFetchCurrent || isLoadingEmployeeSearch || isLoadingSave) && (
        <Loader />
      )}
      {/* Hide panel body while save is in-flight so only the loader is visible */}
      {!(isLoadingFetchCurrent || isLoadingEmployeeSearch || isLoadingSave) && (
      <div
        className="py-[39px]! px-[31px]! !mt-[40px]"
        style={{ backgroundColor: bgColor }}
      >
        <h2 className="h2 m-0!">{title}</h2>
        <h3 className="h3 mt-10!">Find an employee by Name or NUID</h3>

        <div className="flex flex-col gap-3 mt-4">
          {/* Search Form */}
          <SearchForm
            searchOptions={searchOptions}
            fieldsByMode={fieldsByMode}
            fieldMeta={fieldMeta}
            values={values}
            errors={errors}
            searchBy={searchType}
            onSearchTypeChange={handleSearchTypeChange}
            onFieldChange={handleFieldChange}
            onSearch={handleSearch}
            isLoading={isLoadingEmployeeSearch}
            columnLayout={true}
            formId="editPanel"
            submitButtonVariant="secondary"
          />

          {/* Search error banner — surfaced from the searchAvailableEmployees call */}
          {employeeSearchError && (
            <p role="alert" className="p4 -medium" style={{ color: '#c0392b' }}>
              {employeeSearchError}
            </p>
          )}

          {/* Search Results — visible once a search runs or a record is moved in.
              The Add control moves the row into the Approver Group table. */}
          {showResultsSection && !employeeSearchError && (
            <div>
              <h3 className="h3">Search Results</h3>
              {!showNoEmployeesFound && (
                <p className="p3 -book mt-2 mb-3">
                  Found {employees.length} {employees.length === 1 ? 'result' : 'results'}
                </p>
              )}
              {showNoEmployeesFound ? (
                <div role="alert" className="mt-8! p4">
                  <div className="flex items-center gap-3">
                    <img
                      src="/assets/images/alertSolid.svg"
                      alt="Alert"
                      className="w-8! h-8! shrink-0"
                    />
                    <div className="h3">No Results Found</div>
                  </div>
                  <div className="p3 -book mt-2! ml-10!">
                    Please modify your search
                  </div>
                </div>
              ) : (
                <div
                  className={
                    employees.length > 10
                      ? 'bg-white max-h-[700px] overflow-auto'
                      : 'bg-white'
                  }
                >
                  <Table
                    columns={resultColumns}
                    rows={employeeRows}
                    stickyHeader={employees.length > 10}
                    onActionClick={(rowData) =>
                      handleAddToGroup(String(rowData.id))
                    }
                  />
                </div>
              )}
            </div>
          )}

          <hr className="-variation-1" />
          <h3 className="h3 m-0!">{mode === 'requestor' ? 'Current Requestor Proxy' : 'Approver Group'}</h3>
          {mode !== 'requestor' && (
            <>
              <LabeledValue
                label="Group ID"
                value={row.approvalGrpId ?? row.groupId ?? ''}
                customStyles="m-0!"
              />
              <LabeledValue
                label="Primary Approver"
                value={row.approver ?? row.primaryNuid ?? ''}
                customStyles="m-0!"
              />
            </>
          )}
        </div>

        {/* Approver Group table — seeded from fetchCurrentAP/RP; the Remove
            control moves a row into the Search Results table above.
            Scrolls independently with sticky header when more than 10 rows.
            The datepicker calendar uses position:fixed so it is not clipped
            by this overflow container. */}
        <div
          className={
            approverGroup.length > 10
              ? 'bg-white! mt-4! max-h-[700px] overflow-auto'
              : 'mt-4! bg-white!'
          }
        >
          {proxyRows.length === 0 ? (
            <div role="alert" className="mt-8! p4">
              <div className="flex items-center gap-3">
                <img
                  src="/assets/images/alertSolid.svg"
                  alt="Alert"
                  className="w-8! h-8! shrink-0"
                />
                <div className="h3">None assigned.</div>
              </div>
            </div>
          ) : (
            <Table
              columns={approverColumns}
              rows={proxyRows}
              stickyHeader={approverGroup.length > 10}
              onActionClick={(rowData) =>
                handleRemoveFromGroup(String(rowData.id))
              }
            />
          )}
        </div>

        {/* Save error banner — surfaced from the assignApproverProxies / assignRequestorProxies call */}
        {saveError && (
          <p
            role="alert"
            className="p4 -medium mt-4!"
            style={{ color: '#c0392b' }}
          >
            {saveError}
          </p>
        )}

        {/* Action Buttons — always visible so users can save an empty proxy list */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 mt-8!">
          <button
            type="button"
            className="button -secondary w-full md:w-auto px-4 py-2 border border-[#0074AD] text-[#0074AD] rounded-sm font-semibold text-sm hover:bg-[#F0F4F7] whitespace-nowrap"
            onClick={() => setShowCancelModal(true)}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSaveDisabled}
            className="button -primary -light w-full md:w-auto px-4 py-2 bg-[#0074AD] text-white rounded-sm font-semibold text-sm hover:bg-[#005A8A] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            onClick={handleSave}
          >
            Save
          </button>
        </div>

        {/* Cancel Confirmation Modal */}
        <Modal
          isOpen={showCancelModal}
          title="Leave without saving?"
          clearBtn="Cancel"
          submitBtn="Yes, Leave Without Saving"
          iconSrc="/assets/images/alertSolid.svg"
          iconAlt="alert icon"
          onSecondary={() => setShowCancelModal(false)}
          onPrimary={handleCancelConfirm}
          primaryBtnVariant="secondary"
        >
          <div className="p3 -book">
            You have unsaved changes remaining. If you wish to keep them cancel
            this action and then press &ldquo;save&rdquo;
          </div>
        </Modal>

        {/* Confirm Save Modal — shown when the proxy list is empty */}
        <Modal
          isOpen={showConfirmSaveModal}
          title="Confirm Save"
          clearBtn="Cancel"
          submitBtn="Confirm"
          iconSrc="/assets/images/alertSolid.svg"
          iconAlt="alert icon"
          onSecondary={() => setShowConfirmSaveModal(false)}
          onPrimary={() => {
            setShowConfirmSaveModal(false)
            void executeSave()
          }}
          primaryBtnVariant="primary"
        >
          <div className="p3 -book">
            Are you sure you want assign none proxy list to selected approval
            group?
          </div>
        </Modal>

        {/* Success Modal */}
        <Modal
          isOpen={showSuccessModal}
          title="Successfully Saved"
          onClose={() => setShowSuccessModal(false)}
          iconSrc="/assets/images/checkmark-circle-solid.svg"
          iconAlt="success checkmark"
        >
          <span className="p3 -book">
            The changes you have made have been successfully saved.
          </span>
        </Modal>
      </div>
      )}
    </>
  )
}

export default ProxyEditPanel
